import {
  Box,
  Grid,
  Stack,
  Toolbar,
  Typography,
  TextField,
  CircularProgress,
  Button,
  IconButton,
  Paper,
  Divider,
  Chip,
  Badge,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API } from '../../../utils/Api';
import {
  CloseOutlined,
  Inventory2Outlined,
  KeyboardArrowLeft,
  ChecklistOutlined,
  AddShoppingCartOutlined,
  SendOutlined,
  CategoryOutlined,
  NumbersOutlined,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

const StockRequest = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const token = localStorage.getItem('token');
  const outletData = JSON.parse(localStorage.getItem('outletData') || '{}');

  const editIndent = location.state?.indent || null;
  const isEditMode = location.state?.mode === 'edit' && !!editIndent;
  const isCriticalStockMode = location.state?.mode === 'critical-stock';

  const [allRawMaterials, setAllRawMaterials] = useState([]);
  const [outletStockMaterials, setOutletStockMaterials] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedItems, setSelectedItems] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('ALL');

  const normalize = (value) => String(value ?? '').toLowerCase().trim();

  const getRawMeta = (item) => {
    const raw = item?.rawMaterial || item || {};

    return {
      id: raw._id,
      name: raw.name || '',
      unit: raw.unit || '',
      mainCategoryId: raw.mainCategory?._id || '',
      mainCategoryName: raw.mainCategory?.name || '',
      subCategoryName: raw.subCategory?.name || '',
      currentStock: Number(item?.currentStock ?? raw?.currentStock ?? 0),
      stockInfo:
        item?.stockInfo ||
        (Number(item?.currentStock ?? raw?.currentStock ?? 0) <= 0
          ? 'Out of Stock'
          : 'In Stock'),
    };
  };

  const formatStock = (value) => {
    const n = Number(value || 0);
    return Number.isInteger(n) ? n : n.toFixed(2);
  };

  const fetchRawMaterials = async () => {
    setLoading(true);

    try {
      const response = await axios.get(`${API.BASE_URL}raw-material`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          limit: 100000,
        },
      });

      const rawMaterials = response?.data?.data?.rawMaterials || [];

      const activeRawMaterials = rawMaterials.filter(
        (item) => item?.isDelete === false
      );

      setAllRawMaterials(activeRawMaterials);
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch raw materials.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllRawMaterials = async () => {
    setLoading(true);

    try {
      const response = await axios.get(
        `${API.BASE_URL}stock-transactions/getOutletCurrentStock`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            outletId: outletData?._id,
            limit: 100000,
          },
        }
      );

      const totalRecords = response?.data?.data?.totalRecords;

      if (
        typeof totalRecords === 'string' &&
        totalRecords.toLowerCase().includes('no records')
      ) {
        setOutletStockMaterials([]);
      } else {
        setOutletStockMaterials(Array.isArray(totalRecords) ? totalRecords : []);
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch raw materials stock.');
    } finally {
      setLoading(false);
    }
  };

  const rawMaterialsWithStock = useMemo(() => {
    const stockMap = new Map();

    outletStockMaterials.forEach((item) => {
      const rawId = item?.rawMaterial?._id || item?._id;
      if (rawId) {
        stockMap.set(rawId, item);
      }
    });

    return allRawMaterials
      .filter((raw) => raw?.isDelete === false)
      .map((raw) => {
        const stockItem = stockMap.get(raw._id);

        return {
          rawMaterial: raw,
          currentStock: Number(stockItem?.currentStock ?? 0),
          stockInfo:
            stockItem?.stockInfo ||
            (Number(stockItem?.currentStock ?? 0) <= 0
              ? 'Out of Stock'
              : 'In Stock'),
        };
      });
  }, [allRawMaterials, outletStockMaterials]);

  const categories = useMemo(() => {
    const map = new Map();

    rawMaterialsWithStock.forEach((item) => {
      const { mainCategoryId, mainCategoryName } = getRawMeta(item);
      const key = mainCategoryId || mainCategoryName;

      if (key && !map.has(key)) {
        map.set(key, {
          id: key,
          name: mainCategoryName || 'Uncategorized',
        });
      }
    });

    return Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [rawMaterialsWithStock]);

  const filteredRawMaterials = useMemo(() => {
    const q = normalize(searchQuery);
    const words = q ? q.split(/\s+/).filter(Boolean) : [];

    return rawMaterialsWithStock.filter((item) => {
      const meta = getRawMeta(item);
      const categoryKey = meta.mainCategoryId || meta.mainCategoryName;

      const matchesCategory =
        selectedCategory === 'ALL' || categoryKey === selectedCategory;

      if (!matchesCategory) return false;
      if (!words.length) return true;

      const haystack = normalize(
        [
          meta.name,
          meta.mainCategoryName,
          meta.subCategoryName,
          meta.unit,
          meta.currentStock,
          meta.stockInfo,
        ].join(' ')
      );

      return words.every((word) => haystack.includes(word));
    });
  }, [rawMaterialsWithStock, searchQuery, selectedCategory]);

  useEffect(() => {
    fetchRawMaterials();
    fetchAllRawMaterials();
  }, []);

  useEffect(() => {
    if (!isEditMode || !editIndent?.items?.length) return;

    const existingSelected = [];
    const existingQuantities = {};

    editIndent.items.forEach((it) => {
      const rawMaterialId = it?.rawMaterial?._id;
      const requestedQuantity = it?.requestedQuantity;

      if (rawMaterialId) {
        existingSelected.push(rawMaterialId);
        existingQuantities[rawMaterialId] = requestedQuantity || '';
      }
    });

    setSelectedItems(existingSelected);
    setQuantities(existingQuantities);
  }, [isEditMode, editIndent]);

  useEffect(() => {
    const criticalItems = location.state?.criticalItems || [];

    if (location.state?.mode !== 'critical-stock' || !criticalItems.length) return;

    const selected = [];
    const qtyMap = {};

    criticalItems.forEach((item) => {
      const rawId = item?.rawMaterial?._id;

      if (rawId) {
        selected.push(rawId);
        qtyMap[rawId] = item?.requestedQuantity || 1;
      }
    });

    setSelectedItems(selected);
    setQuantities(qtyMap);

    setAllRawMaterials((prev) => {
      const existingIds = new Set(prev.map((i) => getRawMeta(i).id));

      const missingItems = criticalItems
        .filter((item) => item?.rawMaterial?._id && !existingIds.has(item.rawMaterial._id))
        .map((item) => ({
          rawMaterial: item.rawMaterial,
          currentStock: item?.currentStock ?? item?.rawMaterial?.currentStock ?? 0,
          stockInfo:
            item?.stockInfo ||
            (Number(item?.currentStock ?? item?.rawMaterial?.currentStock ?? 0) <= 0
              ? 'Out of Stock'
              : 'In Stock'),
        }));

      return [...prev, ...missingItems];
    });
  }, [location.state]);

  // const handleQuantityChange = (itemId, inputValue) => {
  //   const numericValue = parseFloat(inputValue);

  //   setQuantities((prev) => {
  //     const updated = { ...prev };

  //     if (!inputValue || isNaN(numericValue) || numericValue <= 0) {
  //       delete updated[itemId];
  //     } else {
  //       updated[itemId] = inputValue;
  //     }

  //     return updated;
  //   });

  //   setSelectedItems((prevSelected) => {
  //     if (!isNaN(numericValue) && numericValue > 0) {
  //       if (!prevSelected.includes(itemId)) {
  //         return [...prevSelected, itemId];
  //       }
  //       return prevSelected;
  //     }

  //     return prevSelected.filter((id) => id !== itemId);
  //   });
  // };

  const handleQuantityChange = (itemId, inputValue) => {
    const numericValue = parseFloat(inputValue);

    setQuantities((prev) => ({
      ...prev,
      [itemId]: inputValue, // always keep value (even empty string)
    }));

    setSelectedItems((prevSelected) => {
      // Only ADD if valid number > 0
      if (!isNaN(numericValue) && numericValue > 0) {
        if (!prevSelected.includes(itemId)) {
          return [...prevSelected, itemId];
        }
      }
      return prevSelected;
    });
  };

  const handleRemoveSelectedItem = (itemId) => {
    setSelectedItems((prev) => prev.filter((id) => id !== itemId));
    setQuantities((prev) => {
      const updated = { ...prev };
      delete updated[itemId];
      return updated;
    });
  };

  const handleSubmit = async () => {
    if (!selectedItems.length) {
      toast.error('Please select at least one item by adding quantities.');
      return;
    }

    const items = selectedItems
      .map((itemId) => ({
        rawMaterial: itemId,
        requestedQuantity: Number(quantities[itemId]),
      }))
      .filter(
        (item) =>
          item.rawMaterial &&
          item.requestedQuantity &&
          !Number.isNaN(item.requestedQuantity) &&
          item.requestedQuantity > 0
      );

    if (!items.length) {
      toast.error('Please enter valid quantities for selected items.');
      return;
    }

    try {
      setLoading(true);

      if (isEditMode) {
        const response = await axios.put(
          `${API.BASE_URL}stock-requisition`,
          {
            _id: editIndent._id,
            items,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!response?.data) throw new Error('Update failed.');
        toast.success('Indent updated successfully.');
      } else {
        const response = await axios.post(
          `${API.BASE_URL}stock-requisition`,
          {
            outlet: outletData?._id,
            items,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!response?.data) throw new Error('Submission failed.');
        toast.success('Stock request sent successfully.');
      }

      setSelectedItems([]);
      setQuantities({});
      setSearchQuery('');
      setSelectedCategory('ALL');
      fetchAllRawMaterials();
      navigate('/stock-requistion-requests');
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.data?.title ||
        error?.response?.data?.error ||
        (isEditMode
          ? 'Failed to update indent.'
          : 'Failed to send stock request.')
      );
    } finally {
      setLoading(false);
    }
  };

  const hasSelection = selectedItems.length > 0;

  const totalQuantity = useMemo(() => {
    return selectedItems.reduce((sum, id) => {
      const value = Number(quantities[id]);
      return sum + (!Number.isNaN(value) ? value : 0);
    }, 0);
  }, [selectedItems, quantities]);

  const selectedMaterialsMap = useMemo(() => {
    const map = new Map();
    rawMaterialsWithStock.forEach((item) => {
      const meta = getRawMeta(item);
      if (meta.id) map.set(meta.id, meta);
    });
    return map;
  }, [rawMaterialsWithStock]);

  const headerTitle = useMemo(
    () =>
      isEditMode
        ? 'Edit Stock Requisition'
        : isCriticalStockMode
          ? 'Critical Stock Requisition'
          : 'Stock Requisition',
    [isEditMode, isCriticalStockMode]
  );

  const headerDescription = useMemo(
    () =>
      isEditMode
        ? 'Update item quantities or add new raw materials to this indent.'
        : isCriticalStockMode
          ? 'Review critical stock items and send a quick stock request.'
          : 'Choose raw materials, enter quantities, and raise a request.',
    [isEditMode, isCriticalStockMode]
  );

  const renderQuantityInput = (itemId, width = 112) => (
    <TextField
      size="small"
      placeholder="Qty"
      value={quantities[itemId] || ''}
      onChange={(e) => handleQuantityChange(itemId, e.target.value)}
      onKeyDown={(e) => {
        const allowedControlKeys = [
          'Backspace',
          'Tab',
          'ArrowLeft',
          'ArrowRight',
          'Delete',
          'Home',
          'End',
          '.',
        ];
        if (allowedControlKeys.includes(e.key)) return;
        if (!/[0-9]/.test(e.key)) e.preventDefault();
      }}
      sx={{
        width,
        '& .MuiOutlinedInput-root': {
          height: 42,
          borderRadius: 2.5,
          backgroundColor: '#fff',
          fontWeight: 800,
        },
        '& input': {
          textAlign: 'right',
          fontWeight: 800,
        },
      }}
    />
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background:
          'radial-gradient(circle at top left, rgba(0,48,73,0.08), transparent 32%), linear-gradient(135deg, #f6f8fb 0%, #edf3f8 100%)',
      }}
    >
      <Toolbar />

      <Box sx={{ maxWidth: 1360, mx: 'auto', px: { xs: 2, md: 3 }, pb: 4 }}>
        <Paper
          elevation={0}
          sx={{
            mt: 4,
            mb: 3,
            p: { xs: 2.2, md: 3 },
            borderRadius: 5,
            overflow: 'hidden',
            position: 'relative',
            color: '#fff',
            background:
              'linear-gradient(135deg, #003049 0%, #07516f 55%, #0f6b8c 100%)',
            boxShadow: '0 18px 45px rgba(0,48,73,0.22)',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            spacing={2}
            sx={{ position: 'relative', zIndex: 1 }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <IconButton
                onClick={() => navigate('/managerDashboard')}
                sx={{
                  color: '#003049',
                  bgcolor: '#fff',
                  '&:hover': { bgcolor: '#eef6fb' },
                }}
              >
                <KeyboardArrowLeft />
              </IconButton>

              <Box
                sx={{
                  width: 58,
                  height: 58,
                  borderRadius: 4,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: 'rgba(255,255,255,0.15)',
                  border: '1px solid rgba(255,255,255,0.26)',
                }}
              >
                <Inventory2Outlined sx={{ fontSize: 31 }} />
              </Box>

              <Box>
                <Typography variant="h5" fontWeight={900}>
                  {headerTitle}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ color: 'rgba(255,255,255,0.82)', mt: 0.5 }}
                >
                  {headerDescription}
                </Typography>

                {isEditMode && (
                  <Chip
                    size="small"
                    label={`Indent No: ${editIndent?.indentNumber || '-'}`}
                    sx={{
                      mt: 1,
                      color: '#003049',
                      bgcolor: '#fff',
                      fontWeight: 800,
                    }}
                  />
                )}
              </Box>
            </Stack>

            <Stack direction="row" spacing={1.2} flexWrap="wrap">
              <Chip
                icon={<ChecklistOutlined />}
                label={`${selectedItems.length} Items`}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.16)',
                  color: '#fff',
                  fontWeight: 800,
                  border: '1px solid rgba(255,255,255,0.22)',
                  '& .MuiChip-icon': { color: '#fff' },
                }}
              />

              <Chip
                label={`Total Qty: ${totalQuantity}`}
                sx={{
                  bgcolor: '#fff',
                  color: '#003049',
                  fontWeight: 900,
                }}
              />
            </Stack>
          </Stack>
        </Paper>

        <Grid container spacing={2.5}>
          <Grid item xs={12} md={7}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 5,
                overflow: 'hidden',
                border: '1px solid #dce6ee',
                boxShadow: '0 16px 40px rgba(15,23,42,0.08)',
                height: '100%',
              }}
            >
              <Box
                sx={{
                  p: 2.5,
                  background:
                    'linear-gradient(135deg, #ffffff 0%, #f7fafc 100%)',
                  borderBottom: '1px solid #e2eaf1',
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1.5}
                >
                  <Box>
                    <Typography variant="subtitle1" fontWeight={900} color="#003049">
                      {isEditMode ? 'Add More Materials' : 'Raw Materials'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      List is shown by default. Use search or category to filter.
                    </Typography>
                  </Box>

                  <Chip
                    size="small"
                    icon={<SearchIcon />}
                    label={`${filteredRawMaterials.length} / ${rawMaterialsWithStock.length} materials`}
                    variant="outlined"
                    sx={{ fontWeight: 800, bgcolor: '#fff' }}
                  />
                </Stack>

                <Grid container spacing={1.4} sx={{ mt: 2 }}>
                  <Grid item xs={12} md={7}>
                    <TextField
                      fullWidth
                      placeholder="Search material, category, unit, stock..."
                      variant="outlined"
                      size="small"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: searchQuery ? (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setSearchQuery('')}>
                              <CloseOutlined fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: 48,
                          borderRadius: 3,
                          backgroundColor: '#fff',
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={5}>
                    <FormControl
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: 48,
                          borderRadius: 3,
                          backgroundColor: '#fff',
                        },
                      }}
                    >
                      <InputLabel>Category</InputLabel>
                      <Select
                        label="Category"
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        startAdornment={
                          <InputAdornment position="start">
                            <CategoryOutlined fontSize="small" />
                          </InputAdornment>
                        }
                      >
                        <MenuItem value="ALL">All Categories</MenuItem>
                        {categories.map((category) => (
                          <MenuItem key={category.id} value={category.id}>
                            {category.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ p: 2.5, height: '62vh', overflowY: 'auto' }}>
                {loading ? (
                  <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}>
                    <Stack alignItems="center" spacing={1.5}>
                      <CircularProgress size={32} />
                      <Typography variant="body2" color="text.secondary">
                        Fetching raw materials with stock...
                      </Typography>
                    </Stack>
                  </Box>
                ) : filteredRawMaterials?.length > 0 ? (
                  <Grid container spacing={1.5}>
                    {filteredRawMaterials.map((item) => {
                      const {
                        id,
                        name,
                        unit,
                        mainCategoryName,
                        subCategoryName,
                        currentStock,
                        stockInfo,
                      } = getRawMeta(item);

                      if (!id) return null;

                      const isSelected = selectedItems.includes(id);
                      const isOutOfStock = stockInfo === 'Out of Stock' || Number(currentStock) <= 0;

                      return (
                        <Grid item xs={12} key={id}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 1.6,
                              borderRadius: 3.5,
                              border: isSelected
                                ? '2px solid #003049'
                                : '1px solid #e2eaf1',
                              background: isSelected
                                ? 'linear-gradient(135deg, #eef8ff 0%, #ffffff 100%)'
                                : isOutOfStock
                                  ? 'linear-gradient(135deg, #fff7f8 0%, #ffffff 100%)'
                                  : '#ffffff',
                              display: 'flex',
                              alignItems: { xs: 'stretch', sm: 'center' },
                              justifyContent: 'space-between',
                              flexDirection: { xs: 'column', sm: 'row' },
                              gap: 1.5,
                              transition: '0.2s ease',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 12px 28px rgba(15,23,42,0.09)',
                                borderColor: '#b7c9d8',
                              },
                            }}
                          >
                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                              <Badge
                                color="primary"
                                variant="dot"
                                invisible={!isSelected}
                              >
                                <Box
                                  sx={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 3,
                                    bgcolor: isSelected ? '#003049' : '#f0f5f8',
                                    color: isSelected ? '#fff' : '#607d8b',
                                    display: 'grid',
                                    placeItems: 'center',
                                    flexShrink: 0,
                                  }}
                                >
                                  <Inventory2Outlined fontSize="small" />
                                </Box>
                              </Badge>

                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  variant="caption"
                                  fontWeight={900}
                                  sx={{
                                    textTransform: 'capitalize',
                                    color: '#17212b',
                                    whiteSpace: 'normal',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 'unset',
                                    WebkitBoxOrient: 'vertical',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {name}
                                </Typography>

                                <Stack direction="row" spacing={1} alignItems="center" mt={0.7} flexWrap="wrap">
                                  {!!mainCategoryName && (
                                    <Chip
                                      size="small"
                                      label={mainCategoryName}
                                      sx={{
                                        height: 22,
                                        fontSize: 8,
                                        bgcolor: '#f5f8fb',
                                        fontWeight: 700,
                                        textTransform: 'capitalize',
                                      }}
                                    />
                                  )}
                                  <Typography variant="caption" color="text.secondary">
                                    Unit: {unit || '-'}
                                  </Typography>
                                </Stack>
                              </Box>
                            </Stack>

                            <Stack
                              direction="row"
                              spacing={1.2}
                              alignItems="center"
                              justifyContent={{ xs: 'space-between', sm: 'flex-end' }}
                              sx={{ minWidth: { sm: 260 } }}
                            >
                              <Chip
                                label={
                                  <>
                                    <span style={{ fontWeight: 600 }}>Stock:</span>{' '}
                                    {formatStock(currentStock)} {unit}
                                  </>
                                }
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  height: 26,
                                  fontSize: 11,
                                  borderRadius: 2,
                                  bgcolor: isOutOfStock ? '#fff1f2' : '#eef8f3',
                                  color: isOutOfStock ? '#991b1b' : '#166534',
                                  border: isOutOfStock
                                    ? '1px solid #fecaca'
                                    : '1px solid #ccebd9',
                                }}
                              />

                              {renderQuantityInput(id, 118)}
                            </Stack>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                ) : (
                  <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}>
                    <Stack alignItems="center" spacing={0.8}>
                      <Typography variant="body1" fontWeight={900} color="#003049">
                        No material found
                      </Typography>
                      <Typography variant="caption" color="text.secondary" textAlign="center">
                        Try changing the search text or selected category.
                      </Typography>
                    </Stack>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 5,
                overflow: 'hidden',
                border: '1px solid #dce6ee',
                boxShadow: '0 16px 40px rgba(15,23,42,0.08)',
                position: { md: 'sticky' },
                top: { md: 88 },
              }}
            >
              <Box
                sx={{
                  p: 2.5,
                  background:
                    'linear-gradient(135deg, #ffffff 0%, #f8fbfd 100%)',
                  borderBottom: '1px solid #e2eaf1',
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="subtitle1" fontWeight={900} color="#003049">
                      Request Summary
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Review selected items before submission.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 3,
                      bgcolor: hasSelection ? '#003049' : '#eef2f5',
                      color: hasSelection ? '#fff' : '#90a4ae',
                      display: 'grid',
                      placeItems: 'center',
                    }}
                  >
                    <AddShoppingCartOutlined />
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1} mt={2} flexWrap="wrap">
                  <Chip
                    label={`${selectedItems.length} Selected`}
                    sx={{ fontWeight: 800 }}
                    color={hasSelection ? 'primary' : 'default'}
                  />
                  <Chip
                    label={`${totalQuantity} Qty`}
                    variant="outlined"
                    sx={{ fontWeight: 800, bgcolor: '#fff' }}
                  />
                </Stack>
              </Box>

              <Box sx={{ p: 2.2, maxHeight: '54vh', overflowY: 'auto' }}>
                {hasSelection ? (
                  <Stack spacing={1.3}>
                    {selectedItems
                      .sort((a, b) => {
                        const getName = (itemId) => {
                          let meta = selectedMaterialsMap.get(itemId);

                          if (!meta) {
                            const fallbackItem = editIndent?.items?.find(
                              (it) => it?.rawMaterial?._id === itemId
                            );

                            if (!fallbackItem) return '';

                            return (fallbackItem?.rawMaterial?.name || '');
                          }

                          return meta.name || '';
                        };

                        return getName(a).toLowerCase().localeCompare(getName(b).toLowerCase());
                      })
                      .map((itemId, index) => {
                        let meta = selectedMaterialsMap.get(itemId);

                        if (!meta) {
                          const fallbackItem = editIndent?.items?.find(
                            (it) => it?.rawMaterial?._id === itemId
                          );

                          if (!fallbackItem) return null;

                          meta = {
                            name: fallbackItem?.rawMaterial?.name || 'Raw Material',
                            unit: fallbackItem?.rawMaterial?.unit || '',
                            currentStock: fallbackItem?.currentStock || 0,
                          };
                        }

                        return (
                          <Paper
                            key={itemId}
                            elevation={0}
                            sx={{
                              p: 1.5,
                              borderRadius: 3.5,
                              border: '1px solid #e2eaf1',
                              background:
                                'linear-gradient(135deg, #f9fcff 0%, #ffffff 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.3,
                              transition: '0.2s ease',
                              '&:hover': {
                                boxShadow: '0 10px 24px rgba(15,23,42,0.07)',
                              },
                            }}
                          >
                            <Box
                              sx={{
                                width: 30,
                                height: 30,
                                borderRadius: '50%',
                                bgcolor: '#e7f0f6',
                                color: '#003049',
                                display: 'grid',
                                placeItems: 'center',
                                fontSize: 13,
                                fontWeight: 900,
                                flexShrink: 0,
                              }}
                            >
                              {index + 1}
                            </Box>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="subtitle2"
                                fontWeight={900}
                                sx={{
                                  textTransform: 'capitalize',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  color: '#17212b',
                                }}
                              >
                                {meta.name}
                              </Typography>

                              <Stack direction="row" spacing={1} alignItems="center" mt={0.4}>
                                <Typography variant="caption" color="text.secondary">
                                  Unit: {meta.unit || '-'}
                                </Typography>
                              </Stack>
                            </Box>

                            {renderQuantityInput(itemId, 96)}

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ minWidth: 28, fontWeight: 800 }}
                            >
                              {meta.unit}
                            </Typography>

                            <IconButton
                              onClick={() => handleRemoveSelectedItem(itemId)}
                              size="small"
                              sx={{
                                color: '#d32f2f',
                                bgcolor: '#fff1f1',
                                '&:hover': { bgcolor: '#ffe0e0' },
                              }}
                            >
                              <CloseOutlined fontSize="small" />
                            </IconButton>
                          </Paper>
                        );
                      })}
                  </Stack>
                ) : (
                  <Box sx={{ height: 300, display: 'grid', placeItems: 'center' }}>
                    <Stack alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          width: 74,
                          height: 74,
                          borderRadius: '50%',
                          bgcolor: '#f0f5f8',
                          display: 'grid',
                          placeItems: 'center',
                        }}
                      >
                        <Inventory2Outlined sx={{ fontSize: 34, color: '#90a4ae' }} />
                      </Box>

                      <Typography variant="body2" fontWeight={900} color="#003049">
                        No items selected
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        textAlign="center"
                        sx={{ maxWidth: 260 }}
                      >
                        Enter quantity for any raw material to prepare your request.
                      </Typography>
                    </Stack>
                  </Box>
                )}
              </Box>

              <Divider />

              <Box sx={{ p: 2.2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate(-1)}
                    sx={{
                      height: 46,
                      textTransform: 'none',
                      borderRadius: 3,
                      fontWeight: 900,
                      borderColor: '#cfdbe5',
                      color: '#003049',
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={!loading ? <SendOutlined /> : null}
                    onClick={handleSubmit}
                    disabled={!hasSelection || loading}
                    sx={{
                      height: 46,
                      borderRadius: 3,
                      fontWeight: 900,
                      fontSize: 13,
                      textTransform: 'none',
                      background:
                        'linear-gradient(135deg, #003049 0%, #0f5f7f 100%)',
                      boxShadow: '0 10px 22px rgba(0,48,73,0.22)',
                      '&:hover': {
                        background:
                          'linear-gradient(135deg, #002433 0%, #0b4d68 100%)',
                      },
                      '&.Mui-disabled': {
                        background: '#c7cdd2',
                        color: '#fff',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {loading
                      ? 'Processing...'
                      : isEditMode
                        ? 'Update Indent'
                        : 'Send Request'}
                  </Button>
                </Stack>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default StockRequest;
